// Notification Context - Bildirim Yönetimi
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import {
  subscribeToNotifications,
  markNotificationAsRead,
  getNotificationSettings,
  updateNotificationSettings,
  requestNotificationPermission,
  showBrowserNotification,
  NOTIFICATION_SETTING_STATUS
} from '@3dera/core/services/notifications';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [settings, setSettings] = useState({
    status: NOTIFICATION_SETTING_STATUS.ACTIVE,
    browserNotifications: true,
    emailNotifications: false
  });
  const [permissionStatus, setPermissionStatus] = useState('default');
  const unsubscribeRef = useRef(null);
  // Toast (Chrome bildirimi) tekrarını önlemek için: gösterilmiş id'ler + ilk snapshot bayrağı
  const shownToastIdsRef = useRef(new Set());
  const isFirstSnapshotRef = useRef(true);
  // settings/permission'ı subscription'ı yeniden tetiklemeden okumak için ref'ler
  const settingsRef = useRef(settings);
  const permissionStatusRef = useRef(permissionStatus);
  // Gerçek ayarlar Firestore'dan yüklenene kadar toast gösterme (yanlış varsayılanla toast'u önler)
  const settingsLoadedRef = useRef(false);

  // Ref'leri güncel tut (böylece subscription dependency'sinde yer almazlar)
  useEffect(() => { settingsRef.current = settings; }, [settings]);
  useEffect(() => { permissionStatusRef.current = permissionStatus; }, [permissionStatus]);

  // Bildirim iznini kontrol et ve iste
  useEffect(() => {
    const checkPermission = async () => {
      if ('Notification' in window) {
        setPermissionStatus(Notification.permission);
        
        // Eğer izin verilmemişse ve kullanıcı aktifse, izin iste
        if (Notification.permission === 'default' && currentUser) {
          const result = await requestNotificationPermission();
          setPermissionStatus(result);
        }
      }
    };

    checkPermission();
  }, [currentUser]);

  // Bildirim ayarlarını yükle
  useEffect(() => {
    settingsLoadedRef.current = false;
    const loadSettings = async () => {
      if (currentUser) {
        const result = await getNotificationSettings(currentUser.uid);
        if (result.success) {
          setSettings(result.data);
          settingsRef.current = result.data; // ref'i hemen senkronla (toast kararı için)
        }
      }
      // Yükleme tamamlandı: artık gerçek ayarlarla toast gösterilebilir
      settingsLoadedRef.current = true;
    };

    loadSettings();
  }, [currentUser]);

  // Real-time bildirim dinleyicisi — SADECE kullanıcı (uid) değişince yeniden kurulur.
  // settings/permission ref üzerinden okunur; böylece bunların değişmesi listener'ı
  // yeniden kurup tüm backlog'u yeniden "toast"lamaz.
  const uid = currentUser?.uid;
  useEffect(() => {
    if (!uid) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    // Yeni kullanıcı: toast geçmişini ve ilk-snapshot bayrağını sıfırla
    shownToastIdsRef.current = new Set();
    isFirstSnapshotRef.current = true;

    const unsubscribe = subscribeToNotifications(uid, (newNotifications) => {
      setNotifications(newNotifications);

      const unread = newNotifications.filter(n => n.status === 'unread');
      setUnreadCount(unread.length);

      // İlk snapshot (mevcut backlog): hiçbir şeyi toast'lama, sadece "görüldü" işaretle
      if (isFirstSnapshotRef.current) {
        newNotifications.forEach(n => shownToastIdsRef.current.add(n.id));
        isFirstSnapshotRef.current = false;
        return;
      }

      // Sonraki snapshot'lar: yalnızca daha önce gösterilmemiş YENİ okunmamış bildirimi bir kez göster
      const s = settingsRef.current;
      const canToast =
        settingsLoadedRef.current &&
        s.status === NOTIFICATION_SETTING_STATUS.ACTIVE &&
        s.browserNotifications &&
        permissionStatusRef.current === 'granted';

      unread.forEach(notification => {
        if (shownToastIdsRef.current.has(notification.id)) return;
        // Önce işaretle: izin kapalıyken bile, sonradan açılınca backlog patlamasın
        shownToastIdsRef.current.add(notification.id);
        if (!canToast) return;

        showBrowserNotification(notification.title, {
          body: notification.message,
          icon: '/images/3deralogo.png',
          badge: '/images/3deralogo.png',
          tag: `notification-${notification.id}`,
          onClick: () => {
            // Bildirime tıklandığında ilgili sayfaya yönlendir
            if (notification.metadata?.link) {
              window.location.href = notification.metadata.link;
            }
          }
        });
      });
    });

    unsubscribeRef.current = unsubscribe;

    // Cleanup
    return () => {
      unsubscribe();
      unsubscribeRef.current = null;
    };
  }, [uid]);

  // Bildirimi okundu olarak işaretle
  const markAsRead = useCallback(async (notificationId) => {
    const result = await markNotificationAsRead(notificationId);
    if (result.success) {
      // Local state'i güncelle
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId 
            ? { ...n, status: 'read', readAt: new Date() }
            : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    return result;
  }, []);

  // Tüm bildirimleri okundu olarak işaretle
  // Context'teki mevcut listeyi kullanır, ek Firestore sorgusu yapmaz
  const markAllAsRead = useCallback(async () => {
    if (!currentUser) return { success: false };

    const unreadNotifications = notifications.filter(n => n.status === 'unread');
    if (unreadNotifications.length === 0) return { success: true };

    try {
      const promises = unreadNotifications.map(n => markNotificationAsRead(n.id));
      await Promise.all(promises);

      setNotifications(prev =>
        prev.map(n => n.status === 'unread' ? { ...n, status: 'read', readAt: new Date() } : n)
      );
      setUnreadCount(0);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [currentUser, notifications]);

  // Bildirim ayarlarını güncelle
  const updateSettings = useCallback(async (newSettings) => {
    if (!currentUser) return { success: false };

    const result = await updateNotificationSettings(currentUser.uid, newSettings);
    if (result.success) {
      setSettings(newSettings);
    }
    return result;
  }, [currentUser]);

  // Bildirim izni iste
  const requestPermission = useCallback(async () => {
    const result = await requestNotificationPermission();
    setPermissionStatus(result);
    return result;
  }, []);

  const value = {
    notifications,
    unreadCount,
    settings,
    permissionStatus,
    markAsRead,
    markAllAsRead,
    updateSettings,
    requestPermission
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
